import { useEffect } from "react";

const INTERACTIVE_SELECTOR = [
  "button",
  "a[href]",
  "[role='button']",
  "input[type='checkbox']",
  "input[type='radio']",
  "select",
  "summary",
  "[data-haptic]",
].join(",");

const PATTERNS = {
  light: 8,
  default: 12,
  selection: 8,
  success: [8, 24, 8],
  warning: 18,
  danger: [22, 24, 22],
  strong: 26,
};

function supportsHaptics() {
  return typeof window !== "undefined"
    && typeof navigator !== "undefined"
    && typeof navigator.vibrate === "function"
    && window.matchMedia?.("(pointer: coarse)")?.matches
    && window.localStorage?.getItem("hana_haptics") !== "off";
}

function resolveHapticPattern(element) {
  const explicit = element.getAttribute("data-haptic");
  if (explicit === "off") return null;
  if (explicit && PATTERNS[explicit]) return PATTERNS[explicit];

  const text = `${element.getAttribute("aria-label") || ""} ${element.textContent || ""}`.toLowerCase();
  const classes = element.className?.toString?.().toLowerCase?.() || "";

  if (/supprimer|delete|retirer|urgence|critical|danger/.test(text) || /bg-(red|rose)-/.test(classes)) {
    return PATTERNS.danger;
  }
  if (/valider|confirmer|accept|terminer|pris|résolu|resolu/.test(text) || /bg-(emerald|teal|green)-/.test(classes)) {
    return PATTERNS.success;
  }
  if (element.tagName === "SELECT" || element.matches("input[type='checkbox'], input[type='radio']")) {
    return PATTERNS.selection;
  }
  return PATTERNS.default;
}

function isDisabled(element) {
  return Boolean(
    element.disabled
    || element.getAttribute("aria-disabled") === "true"
    || element.closest("[disabled], [aria-disabled='true']")
  );
}

export default function HapticFeedbackBridge() {
  useEffect(() => {
    if (!supportsHaptics()) return undefined;

    let lastPulseAt = 0;

    const onPointerDown = (event) => {
      if (event.pointerType && event.pointerType !== "touch") return;
      const target = event.target?.closest?.(INTERACTIVE_SELECTOR);
      if (!target || isDisabled(target)) return;
      if (target.closest("input[type='text'], input[type='tel'], input[type='email'], input[type='password'], textarea, [contenteditable='true']")) return;

      const now = Date.now();
      if (now - lastPulseAt < 80) return;
      lastPulseAt = now;

      const pattern = resolveHapticPattern(target);
      if (!pattern) return;

      try {
        navigator.vibrate(pattern);
      } catch {
        // Haptique facultatif : ne jamais bloquer un parcours clinique.
      }
    };

    window.addEventListener("pointerdown", onPointerDown, { capture: true, passive: true });
    return () => window.removeEventListener("pointerdown", onPointerDown, { capture: true });
  }, []);

  return null;
}